// src/pages/offlineSales/components/CloseSessionModal.jsx
import React, { useState, useEffect } from 'react';
import { X, XCircle } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { closeSession, getSessionReport } from '../../../services/api/offlineSales/cashierSessions';
import { formatDateTime } from '../../../utils/dateUtils';
import styles from './OfflineSalesSharedModals.module.css';

const METHOD_LABELS = {
  cash: 'Cash',
  card: 'Card',
  bank_transfer: 'Bank Transfer',
  online: 'Online',
};
const methodLabel = (key) => METHOD_LABELS[key] || key;

const CloseSessionModal = ({ session, onClosed, onClose }) => {
  const { t, formatCurrency } = useLocalization();
  const { error, success } = useAlert();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [closingAmounts, setClosingAmounts] = useState({});
  const [notes, setNotes] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getSessionReport(session.id);
        setReport(result);
        const initAmounts = { cash: '' };
        if (result.paymentBreakdown) {
          Object.keys(result.paymentBreakdown).forEach(m => { initAmounts[m] = ''; });
        }
        setClosingAmounts(initAmounts);
      } catch {
        error('Failed to load session report');
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [session.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getVariance = (method, val) => {
    if (val === '') return null;
    const cv = parseFloat(val) || 0;
    const salesAmount = report?.paymentBreakdown?.[method] || 0;
    const openingCash = parseFloat(session.openingCash) || 0;
    if (method === 'cash') return cv - (openingCash + salesAmount);
    return cv - salesAmount;
  };

  const totalVariance = Object.entries(closingAmounts).reduce((sum, [method, val]) => {
    const v = getVariance(method, val);
    return v !== null ? sum + v : sum;
  }, 0);
  const anyEntered = Object.values(closingAmounts).some(v => v !== '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const closingBreakdown = {};
    Object.entries(closingAmounts).forEach(([method, val]) => {
      closingBreakdown[method] = parseFloat(val) || 0;
    });
    try {
      setSubmitting(true);
      const result = await closeSession(session.id, { closingBreakdown, notes });
      success('Session closed');
      onClosed(result.session);
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to close session');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={`${styles.modal} ${styles.modalLg}`} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>{t('offlineSales.closeSession')} — #{session.id}</h3>
          <button onClick={onClose} className={styles.closeBtn}><X size={18} /></button>
        </div>

        {loading ? (
          <div className={styles.body} style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            Loading...
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <div className={styles.body}>
              {/* Session summary */}
              <div className={styles.reportSection}>
                <h4>{t('offlineSales.sessionSummary')}</h4>
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.openedAt')}</span>
                  <span>{formatDateTime(session.openedAt)}</span>
                </div>
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.openingCash')}</span>
                  <span>{formatCurrency(session.openingCash || 0)}</span>
                </div>
                <div className={styles.reportRow}>
                  <span>{t('offlineSales.totalSales')}</span>
                  <span>{formatCurrency(report?.totalSalesValue || 0)} ({report?.totalSalesCount || 0} orders)</span>
                </div>
              </div>

              {/* Sales payment breakdown */}
              {report?.paymentBreakdown && Object.keys(report.paymentBreakdown).length > 0 && (
                <div className={styles.reportSection}>
                  <h4>{t('offlineSales.paymentBreakdown')}</h4>
                  {Object.entries(report.paymentBreakdown).map(([method, amount]) => (
                    <div key={method} className={styles.reportRow}>
                      <span>{methodLabel(method)}</span>
                      <span>{formatCurrency(amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Closing amount inputs */}
              <div className={styles.reportSection}>
                <h4>{t('offlineSales.closingAmounts')}</h4>
                {Object.entries(closingAmounts).map(([method, val]) => {
                  const variance = getVariance(method, val);
                  return (
                    <div key={method} className={styles.closingRow}>
                      <span className={styles.closingMethodLabel}>{methodLabel(method)}</span>
                      <input
                        type="number" min="0" step="0.01"
                        value={val}
                        onChange={e => setClosingAmounts(prev => ({ ...prev, [method]: e.target.value }))}
                        placeholder="0.00"
                        className={`${styles.input} ${styles.closingInput}`}
                      />
                      {variance !== null && (
                        <span className={`${styles.varianceChip} ${variance === 0 ? styles.varOk : variance > 0 ? styles.varOver : styles.varShort}`}>
                          {variance >= 0 ? '+' : ''}{formatCurrency(Math.abs(variance))}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Total variance */}
              {anyEntered && (
                <div className={styles.reportRowTotal}>
                  <span>{t('offlineSales.totalVariance')}</span>
                  <strong className={totalVariance === 0 ? styles.varianceGood : styles.variance}>
                    {totalVariance >= 0 ? '+' : ''}{formatCurrency(Math.abs(totalVariance))}
                  </strong>
                </div>
              )}

              {/* Notes */}
              <div className={styles.formGroup}>
                <label className={styles.label}>{t('offlineSales.sessionNotes')}</label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  className={styles.textarea}
                />
              </div>
            </div>
            <div className={styles.footer}>
              <button type="button" onClick={onClose} className={styles.cancelBtn}>{t('common.cancel')}</button>
              <button type="submit" disabled={submitting} className={styles.dangerBtn}>
                <XCircle size={14} style={{ marginRight: 4 }} />
                {submitting ? '...' : t('offlineSales.closeSession')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CloseSessionModal;
